import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import { fetchWordOfDay, fetchWordOfDayHistory } from "./vocabularyService";
import "./WordOfDayPage.css";

const difficultyColor = (d) => {
 const map = {
 BEGINNER: "#4caf50", ELEMENTARY: "#8bc34a", INTERMEDIATE: "#ff9800",
 UPPER_INTERMEDIATE: "#ff5722", ADVANCED: "#e91e63", EXPERT: "#9c27b0",
 };
 return map[d?.toUpperCase()] || "#999";
};

const formatDifficulty = (d) =>
 d?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || d;

const formatDate = (dateStr) => {
 if (!dateStr) return "";
 const d = new Date(dateStr + "T00:00:00");
 return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

const WordOfDayPage = () => {
 const navigate = useNavigate();
 const [todayWord, setTodayWord] = useState(null);
 const [history, setHistory] = useState([]);
 const [loading, setLoading] = useState(true);
 const [historyLoading, setHistoryLoading] = useState(false);
 const [offset, setOffset] = useState(0);
 const [totalPages, setTotalPages] = useState(0);
 const LIMIT = 7;

 // Helper function to capitalize part of speech
 const capitalizePartOfSpeech = (pos) => {
 if (!pos) return pos;
 return pos.charAt(0).toUpperCase() + pos.slice(1).toLowerCase();
 };

 useEffect(() => {
 const load = async () => {
 setLoading(true);
 const res = await fetchWordOfDay();
 if (res.status === 200 && res.data?.status) {
 setTodayWord(res.data.data);
 }
 setLoading(false);
 };
 load();
 }, []);

 const loadHistory = useCallback(async () => {
 setHistoryLoading(true);
 const res = await fetchWordOfDayHistory({ limit: LIMIT, offset });
 if (res.status === 200 && res.data?.status) {
 setHistory(res.data.data || []);
 setTotalPages(res.data.totalPages || 0);
 }
 setHistoryLoading(false);
 }, [offset]);

 useEffect(() => { loadHistory(); }, [loadHistory]);

 const currentPage = Math.floor(offset / LIMIT);

 return (
 <>
 <Navbar />
 <div className="wod-page">
 <h1 className="wod-title">📅 Word of the Day</h1>
 <p className="wod-subtitle">A new word every day to expand your vocabulary.</p>

 {loading ? (
 <p className="wod-loading">Loading...</p>
 ) : !todayWord ? (
 <p className="wod-empty">No word of the day available.</p>
 ) : (
 <div className="wod-today-card">
 <div className="wod-today-date">{formatDate(todayWord.date)}</div>
 <div className="wod-today-header">
 <h2 className="wod-today-word">{todayWord.word}</h2>
 <span className="wod-today-difficulty" style={{ background: difficultyColor(todayWord.difficulty) }}>
 {formatDifficulty(todayWord.difficulty)}
 </span>
 </div>

 {(todayWord.pronunciation || todayWord.phoneticSpelling) && (
 <div className="wod-pron-row">
 {todayWord.pronunciation && (
 <span className="wod-pron">
 <span className="wod-pron-label">
 IPA
 <Link to="/study/ipa/lessons" className="wod-ipa-info" title="Learn IPA">ℹ</Link>
 </span>
 {todayWord.pronunciation}
 </span>
 )}
 {todayWord.phoneticSpelling && (
 <span className="wod-phonetic">
 <span className="wod-pron-label">Say</span>
 {todayWord.phoneticSpelling}
 </span>
 )}
 </div>
 )}

 {Array.isArray(todayWord.partOfSpeech) && todayWord.partOfSpeech.length > 0 ? (
 <div style={{ marginTop: '8px' }}>
 {todayWord.partOfSpeech.map((pos, index) => (
 <span key={index} className="wod-pos" style={{ marginRight: '6px' }}>
 {capitalizePartOfSpeech(pos)}
 </span>
 ))}
 </div>
 ) : todayWord.partOfSpeech ? (
 <span className="wod-pos">{capitalizePartOfSpeech(todayWord.partOfSpeech)}</span>
 ) : null}

 <p className="wod-meaning">{todayWord.meaning}</p>

 {todayWord.exampleSentence && (
 <div className="wod-example">
 <span className="wod-example-label">Example</span>
 <p className="wod-example-text">"{todayWord.exampleSentence}"</p>
 </div>
 )}

 {todayWord.etymology && (
 <div className="wod-etymology">
 <span className="wod-etymology-label">📜 Etymology</span>
 <p className="wod-etymology-text">{todayWord.etymology}</p>
 </div>
 )}

 {(todayWord.synonyms?.length > 0 || todayWord.antonyms?.length > 0) && (
 <div className="wod-tags-row">
 {todayWord.synonyms?.length > 0 && (
 <div className="wod-tags-group">
 <span className="wod-tags-label">Synonyms</span>
 <div className="wod-tags">
 {todayWord.synonyms.map((s, i) => <span key={i} className="wod-tag synonym">{s}</span>)}
 </div>
 </div>
 )}
 {todayWord.antonyms?.length > 0 && (
 <div className="wod-tags-group">
 <span className="wod-tags-label">Antonyms</span>
 <div className="wod-tags">
 {todayWord.antonyms.map((a, i) => <span key={i} className="wod-tag antonym">{a}</span>)}
 </div>
 </div>
 )}
 </div>
 )}

 {todayWord.id && (
 <button className="wod-detail-btn" onClick={() => navigate(`/study/vocabulary/${todayWord.id}`)}>
 View Full Details →
 </button>
 )}
 </div>
 )}

 {/* History */}
 <h2 className="wod-history-title">📚 Past Words</h2>

 {historyLoading ? (
 <p className="wod-loading">Loading history...</p>
 ) : history.length === 0 ? (
 <p className="wod-empty">No history available.</p>
 ) : (
 <div className="wod-history-list">
 {history.map((w) => (
 <div key={w.id + "-" + w.date} className="wod-history-card" onClick={() => navigate(`/study/vocabulary/${w.id}`)}>
 <div className="wod-history-date">{formatDate(w.date)}</div>
 <div className="wod-history-info">
 <h3 className="wod-history-word">{w.word}</h3>
 <p className="wod-history-meaning">{w.meaning}</p>
 </div>
 <span className="wod-history-difficulty" style={{ background: difficultyColor(w.difficulty) }}>
 {formatDifficulty(w.difficulty)}
 </span>
 </div>
 ))}
 </div>
 )}

 {totalPages > 1 && (
 <div className="wod-pagination">
 <button disabled={offset === 0} onClick={() => setOffset(offset - LIMIT)}>← Newer</button>
 <span>Page {currentPage + 1} / {totalPages}</span>
 <button disabled={currentPage + 1 >= totalPages} onClick={() => setOffset(offset + LIMIT)}>Older →</button>
 </div>
 )}
 </div>
 <Footer />
 </>
 );
};

export default WordOfDayPage;
